import { useEffect, useState } from 'react'
import { Plus, Trash2, Utensils } from 'lucide-react'
import ErpPeriodDropdown from '../../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import Modal from '../../../../components/ERP/Modal/Modal'
import { patientService } from '../../../../services/patientApi'
import {
  CONSISTENCIES, FOOD_UNITS, MEAL_PRESETS, UNIT_OPTIONS, blankFood
} from './nutritionFormat'

const MEAL_OPTIONS = [
  ...MEAL_PRESETS.map((meal) => ({ value: meal.name, label: meal.name })),
  { value: '__custom', label: 'Outra' }
]
const FOOD_UNIT_OPTIONS = FOOD_UNITS.map((unit) => ({ value: unit, label: unit }))
const STOCK_UNIT_OPTIONS = UNIT_OPTIONS.map((item) => ({ value: item.value, label: item.many }))

function FieldMenu({ value, options, onChange, ariaLabel, compact = false }) {
  return (
    <ErpPeriodDropdown
      className={`patients-field-dropdown${compact ? ' nut-unit-menu' : ''}`}
      label=""
      align="left"
      portal
      ariaLabel={ariaLabel}
      value={value}
      options={options}
      onChange={onChange}
    />
  )
}

const EMPTY = {
  mealPreset: 'Almoço',
  name: 'Almoço',
  consistency: 'oral_pastosa',
  items: [blankFood()],
  trackStock: false,
  volumeMl: '',
  currentStock: '0',
  minStockAlert: '2',
  unitType: 'latas',
  doseAmount: '1',
  notes: ''
}

function fromPrescription(item) {
  if (!item) return { ...EMPTY, items: [blankFood()] }
  const preset = MEAL_PRESETS.find((meal) => meal.name === item.name)
  const foods = (item.items || []).map((food, index) => ({
    key: `food-${index}-${food.food_name}`,
    foodName: food.food_name || '',
    quantity: food.quantity == null ? '' : String(food.quantity),
    unit: food.unit || 'g'
  }))
  return {
    mealPreset: preset ? preset.name : '__custom',
    name: item.name || '',
    consistency: item.consistency || (item.diet_kind === 'oral' ? 'oral_pastosa' : item.route === 'gtt' ? 'enteral_gtt' : 'enteral_sonda'),
    items: foods.length ? foods : [blankFood()],
    trackStock: Boolean(item.track_stock),
    volumeMl: item.volume_ml == null ? '' : String(item.volume_ml),
    currentStock: item.current_stock == null ? '0' : String(item.current_stock),
    minStockAlert: item.min_stock_alert == null ? '' : String(item.min_stock_alert),
    unitType: item.unit_type || 'latas',
    doseAmount: item.dose_amount == null ? '1' : String(item.dose_amount),
    notes: item.notes || ''
  }
}

function NutritionPrescriptionModal({ isOpen, patientId, prescription, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const enteral = form.consistency.startsWith('enteral')

  useEffect(() => {
    if (!isOpen) return
    setForm(fromPrescription(prescription))
    setError('')
  }, [isOpen, prescription])

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }))

  const chooseMeal = (mealPreset) => {
    const preset = MEAL_PRESETS.find((meal) => meal.name === mealPreset)
    setForm((current) => ({
      ...current,
      mealPreset,
      name: preset ? preset.name : (current.mealPreset === '__custom' ? current.name : '')
    }))
  }

  const chooseConsistency = (consistency) => {
    setForm((current) => ({
      ...current,
      consistency,
      trackStock: consistency.startsWith('enteral') ? current.trackStock : false
    }))
  }

  const updateFood = (key, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((food) => (food.key === key ? { ...food, [field]: value } : food))
    }))
  }

  const setFood = (key, field) => (event) => updateFood(key, field, event.target.value)

  const addFood = () => setForm((current) => ({ ...current, items: [...current.items, blankFood()] }))

  const removeFood = (key) => {
    setForm((current) => {
      const items = current.items.filter((food) => food.key !== key)
      return { ...current, items: items.length ? items : [blankFood()] }
    })
  }

  const submit = async (event) => {
    event.preventDefault()
    const items = form.items
      .map((food) => ({ foodName: food.foodName.trim(), quantity: food.quantity, unit: food.unit }))
      .filter((food) => food.foodName)
    const payload = {
      name: form.name,
      consistency: form.consistency,
      items,
      trackStock: enteral && form.trackStock,
      volumeMl: enteral && form.trackStock ? form.volumeMl : '',
      currentStock: prescription ? undefined : (enteral && form.trackStock ? Number(form.currentStock || 0) : 0),
      minStockAlert: enteral && form.trackStock ? form.minStockAlert : '',
      unitType: form.unitType,
      doseAmount: enteral && form.trackStock ? Number(form.doseAmount || 1) : 1,
      notes: form.notes
    }
    setSaving(true)
    setError('')
    try {
      if (prescription) await patientService.updateNutritionPrescription(patientId, prescription.id, payload)
      else await patientService.createNutritionPrescription(patientId, payload)
      onSaved()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível salvar a refeição')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={prescription ? 'Editar refeição' : 'Nova refeição'}
      subtitle="Cardápio, consistência e gramatura de cada alimento"
      icon={<Utensils size={22} />}
      size="large"
      footer={(
        <button type="submit" form="nutrition-prescription-form" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar refeição'}
        </button>
      )}
    >
      <form id="nutrition-prescription-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-span-2">
            <label>Refeição *</label>
            <FieldMenu ariaLabel="Refeição" value={form.mealPreset} options={MEAL_OPTIONS} onChange={chooseMeal} />
          </div>
          {form.mealPreset === '__custom' && (
            <div className="patients-span-2">
              <label>Nome da refeição *</label>
              <input required value={form.name} onChange={set('name')} placeholder="Ex: Ceia reforçada" />
            </div>
          )}
          <div className="patients-span-2">
            <label>Consistência *</label>
            <FieldMenu ariaLabel="Consistência" value={form.consistency} options={CONSISTENCIES} onChange={chooseConsistency} />
          </div>
        </div>

        <section className="nut-plate-editor">
          <div className="nut-plate-head">
            <h3>Itens do prato</h3>
            <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={addFood}>
              <Plus size={14} /> Alimento
            </button>
          </div>
          <div className="nut-item-head" aria-hidden="true">
            <span>Alimento</span>
            <span>Quantidade</span>
            <span>Unidade</span>
            <span />
          </div>
          {form.items.map((food) => (
            <div key={food.key} className="nut-item-row">
              <input
                value={food.foodName}
                placeholder="Ex: Arroz"
                aria-label="Alimento"
                onChange={setFood(food.key, 'foodName')}
              />
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={food.quantity}
                placeholder="50"
                aria-label="Quantidade"
                onChange={setFood(food.key, 'quantity')}
              />
              <FieldMenu
                compact
                ariaLabel="Unidade"
                value={food.unit}
                options={FOOD_UNIT_OPTIONS}
                onChange={(unit) => updateFood(food.key, 'unit', unit)}
              />
              <button type="button" className="patients-icon-btn" aria-label="Remover alimento" onClick={() => removeFood(food.key)}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </section>

        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-span-2">
            <label>Observações</label>
            <textarea value={form.notes} onChange={set('notes')} rows={2} />
          </div>
          {enteral && (
            <div className="patients-span-2 nut-stock-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={form.trackStock}
                  onChange={(event) => setForm((current) => ({ ...current, trackStock: event.target.checked }))}
                />
                Controlar estoque da fórmula
              </label>
            </div>
          )}
          {enteral && form.trackStock && (
            <>
              <div>
                <label>Volume da fórmula (ml)</label>
                <input type="number" min="1" max="5000" step="1" value={form.volumeMl} onChange={set('volumeMl')} />
              </div>
              {!prescription && (
                <div>
                  <label>Estoque atual</label>
                  <input type="number" min="0" step="0.01" value={form.currentStock} onChange={set('currentStock')} />
                </div>
              )}
              <div>
                <label>Alerta de estoque</label>
                <input type="number" min="0" step="0.01" value={form.minStockAlert} onChange={set('minStockAlert')} />
              </div>
              <div>
                <label>Unidade</label>
                <FieldMenu
                  ariaLabel="Unidade de estoque"
                  value={form.unitType}
                  options={STOCK_UNIT_OPTIONS}
                  onChange={(unitType) => setForm((current) => ({ ...current, unitType }))}
                />
              </div>
              <div>
                <label>Consumo por oferta</label>
                <input type="number" min="0.01" step="0.01" value={form.doseAmount} onChange={set('doseAmount')} />
              </div>
            </>
          )}
        </div>
      </form>
    </Modal>
  )
}

export default NutritionPrescriptionModal
